import React from 'react';
import { connect } from 'react-redux';
import * as R from 'ramda';
import { PieChart, Pie, Cell } from 'recharts';
import moment from 'moment';

import DateField from 'material-ui/DatePicker';
import TimeField from 'material-ui/TimePicker';

import StatusChart from '../../components/chats/StatusChart';
import Button from '../../components/basic/Button';

import './ChatStatsContainer.css';

import * as chatStatsActions from '../../actions/entities/ChatStatsActions';

const CUSTOM_TIME_FRAMES = {
  LAST_7_DAYS: {
    key: 'LAST_7_DAYS',
    getRange: () => ({
      from: moment()
        .subtract(7, 'days')
        .startOf('day'),
      to: moment().startOf('day')
    }),
    label: 'last 7 days'
  },
  LAST_WEEK: {
    key: 'LAST_WEEK',
    getRange: () => ({
      from: moment()
        .subtract(1, 'week')
        .startOf('week'),
      to: moment().startOf('week')
    }),
    label: 'last week'
  },
  LAST_30_DAYS: {
    key: 'LAST_30_DAYS',
    getRange: () => ({
      from: moment()
        .subtract(30, 'days')
        .startOf('day'),
      to: moment().startOf('day')
    }),
    label: 'last 30 days'
  },
  LAST_MONTH: {
    key: 'LAST_MONTH',
    getRange: () => ({
      from: moment()
        .subtract(1, 'month')
        .startOf('month'),
      to: moment().startOf('month')
    }),
    label: 'last month'
  }
};

const formatDateTime = (date, time) =>
  date && time ? `${moment(date).format('YYYY-MM-DD')} ${moment(time).format('HH:mm')}` : null;
const parseDateTime = dateTime => (dateTime ? moment(dateTime, 'YYYY-MM-DD HH:mm') : null);

const formatPercentage = val => `${(val * 100).toFixed(2)} %`;

const renderPercentageChart = (pctg, config = {}) => {
  const size = config.size || 32;
  const colors = config.colors || ['#46617c', '#e7ebee'];
  const data = [{ name: 'full', value: pctg, color: colors[0] }, { name: 'empty', value: 1 - pctg, color: colors[1] }];
  return (
    <PieChart width={size} height={size} margin={{ top: 0, left: 0, right: 0, bottom: 0 }}>
      <Pie
        data={data}
        dataKey={'value'}
        innerRadius={'50%'}
        outerRadius={'100%'}
        startAngle={90}
        endAngle={-270}
        isAnimationActive={false}
      >
        {R.map(d => <Cell key={d.name} fill={d.color} stroke="none" />, data)}
      </Pie>
    </PieChart>
  );
};

const renderStatsCount = (num, pctg, finished, label) => (
  <div className="Chat-stats-count">
    {label && <div className="small text-muted">{label}</div>}
    {!isNaN(pctg) && (
      <div className="Chat-percentage-chart">
        {renderPercentageChart(pctg, finished ? { colors: ['#17682c', '#dce8e0'] } : {})}
      </div>
    )}
    <div>{num}x</div>
    {!isNaN(pctg) && <div className="small">{formatPercentage(pctg)}</div>}
  </div>
);

const renderMessage = (key, message, answerCount, finishedCount, totals, noAnswer) => {
  return (
    <div key={key} className={`Chat-answer-container${noAnswer ? ' Chat-no-answer' : ''} row`}>
      <div className="col-sm-7">
        <div className="Chat-message">
          {noAnswer ? <em>No answer</em> : <p dangerouslySetInnerHTML={{ __html: message }} />}
        </div>
      </div>
      <div className="col-sm-5">
        <div className="Chat-stats-counts">
          {renderStatsCount(answerCount, answerCount / totals.questionCount, false)}
          {!noAnswer &&
            renderStatsCount(
              finishedCount,
              totals.finishedCount !== 0 ? finishedCount / totals.finishedCount : 0,
              true
            )}
        </div>
      </div>
    </div>
  );
};

class ChatStatsContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      from: null,
      fromTime: null,
      to: null,
      toTime: null
    };
  }

  componentDidMount() {
    this.loadData();
  }

  loadData = () => {
    const { fetchChatStats, match: { params: { projectId } } } = this.props;
    const { from, fromTime, to, toTime } = this.state;

    const fromFormatted = formatDateTime(from, fromTime);
    const toFormatted = formatDateTime(to, toTime);
    const fromParsed = parseDateTime(fromFormatted);
    const toParsed = parseDateTime(toFormatted);

    fetchChatStats(projectId, fromParsed ? fromParsed.valueOf() : null, toParsed ? toParsed.valueOf() : null);
  };

  handleFromChange = (event, from) => {
    this.setState({ from });
  };

  handleFromTimeChange = (event, fromTime) => {
    this.setState({ fromTime });
  };

  handleToChange = (event, to) => {
    this.setState({ to });
  };

  handleToTimeChange = (event, toTime) => {
    this.setState({ toTime });
  };

  handleCustomTimeFrame = tf => {
    const range = tf.getRange();
    this.setState({
      from: range.from.toDate(),
      fromTime: range.from.toDate(),
      to: range.to.toDate(),
      toTime: range.to.toDate()
    });
  };

  applyFilter = () => {
    this.loadData();
  };

  render() {
    const { currentChatStats, loading } = this.props;
    if (!currentChatStats || !currentChatStats.id || loading) {
      return null;
    }
    const { name, slug, variableStats, questionStats, answerStats } = currentChatStats;
    const reduceToArray = R.reduceBy((acc, val) => [...acc, val], []);
    const answerStatsByDialogueKey = reduceToArray(R.prop('dialogueKey'))(answerStats);
    const questionStatsByDialogueKey = reduceToArray(R.prop('dialogueKey'))(questionStats);
    const dialogueKeys = R.uniq(R.map(R.prop('dialogueKey'), R.sortBy(R.prop('questionOrder'))(questionStats)));
    const reportedVariableStats = R.filter(v => !v.identifying, variableStats || []);
    const variables = R.uniq(
      R.map(
        R.prop('name'),
        R.sortWith([R.descend(R.prop('reported')), R.ascend(R.prop('name'))])(reportedVariableStats)
      )
    );
    const variableStatsByName = reduceToArray(R.prop('name'))(reportedVariableStats);
    return (
      <div className="flex flex1 flex-column Chat-stats-container">
        <div className="padding-1em">
          <div className="flex flex1">
            <div className="flex flex1 flex-column">
              <span className="Card-title">{name || <em>Unnamed</em>}</span>
              <pre className="Card-subtitle">{slug}</pre>
            </div>
            <StatusChart {...currentChatStats} />
          </div>
        </div>
        <div style={{ background: 'white' }}>
          <div className="padding-1em">
            <h6>Filter</h6>
            <div className="row with-padding">
              <div className="col-md-4 col-sm-7">
                <DateField
                  name="filterFromDate"
                  floatingLabelText={'From date'}
                  floatingLabelFixed
                  hintText={'From date'}
                  fullWidth
                  value={this.state.from}
                  onChange={this.handleFromChange}
                />
              </div>
              <div className="col-md-2 col-sm-5">
                <TimeField
                  name="filterFromTime"
                  floatingLabelText={'From time'}
                  floatingLabelFixed
                  hintText="From time"
                  fullWidth
                  format="24hr"
                  value={this.state.fromTime}
                  onChange={this.handleFromTimeChange}
                />
              </div>
              <div className="col-md-4 col-sm-7">
                <DateField
                  name="filterToDate"
                  floatingLabelText={'To date'}
                  floatingLabelFixed
                  hintText={'To date'}
                  fullWidth
                  value={this.state.to}
                  onChange={this.handleToChange}
                />
              </div>
              <div className="col-md-2 col-sm-5">
                <TimeField
                  name="filterToTime"
                  floatingLabelText={'To time'}
                  floatingLabelFixed
                  hintText="To time"
                  fullWidth
                  format="24hr"
                  value={this.state.toTime}
                  onChange={this.handleToTimeChange}
                />
              </div>
            </div>
            <div className="row with-padding">
              <div className="col-sm-12">
                <div style={{ position: 'relative', paddingRight: '120px' }}>
                  {R.map(
                    tf => (
                      <div key={tf.key} style={{ display: 'inline-block' }}>
                        <Button label={tf.label} onClick={() => this.handleCustomTimeFrame(tf)} />
                      </div>
                    ),
                    R.values(CUSTOM_TIME_FRAMES)
                  )}
                  <div style={{ position: 'absolute', right: 0, bottom: 0 }}>
                    <Button primary raised label={'Apply'} onClick={this.applyFilter} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="padding-1em">
            <h6>Variables</h6>
            {variables.length > 0 ? (
              R.map(v => {
                const stats = R.sortWith([R.descend(s => !!s.value), R.descend(R.prop('count'))])(
                  variableStatsByName[v]
                );
                const first = stats[0];
                const totalCount = R.reduce((r, s) => r + s.count, 0, stats);
                return (
                  <div className="Chat-variable-container" key={v}>
                    {first && (
                      <div>
                        <div className="Chat-variable-header row">
                          <div className="col-sm-8">
                            <div className="Text-regular">{first.label || <em>Unnamed</em>}</div>
                            <div className="Text-small">
                              <pre>{first.name}</pre>
                            </div>
                          </div>
                          <div className="col-sm-4">
                            <div className="Chat-stats-counts">
                              <div className="Chat-stats-count">{totalCount}x</div>
                            </div>
                          </div>
                        </div>
                        <div className="Chat-variable-values">
                          {R.map(
                            s => (
                              <div
                                className={`Chat-variable-value${s.value ? '' : ' Chat-no-answer'} row`}
                                key={'key' + s.value}
                              >
                                <div className="col-sm-8">
                                  <div className="Chat-message">
                                    {s.value ? <p dangerouslySetInnerHTML={{ __html: s.value }} /> : <em>No value</em>}
                                  </div>
                                </div>
                                <div className="col-sm-4">
                                  <div className="Chat-stats-counts">
                                    {renderStatsCount(s.count, s.count / totalCount, false)}
                                  </div>
                                </div>
                              </div>
                            ),
                            stats
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }, variables)
            ) : (
              <div className="Empty-message">No variable stats available</div>
            )}
          </div>
          <div className="padding-1em">
            <h6>Messages</h6>
            {questionStats && questionStats.length > 0 ? (
              R.map(k => {
                const qs = questionStatsByDialogueKey[k] || [];
                const as = answerStatsByDialogueKey[k] || [];
                const totals = R.reduce(
                  (r, q) => ({
                    questionCount: r.questionCount + q.questionCount,
                    finishedCount: r.finishedCount + q.finishedCount
                  }),
                  { questionCount: 0, finishedCount: 0 },
                  qs
                );
                const noAnswer = totals.questionCount - R.reduce((r, a) => r + a.answerCount, 0, as);
                return (
                  <div className="Chat-dialogue-container" key={k}>
                    <div className="Chat-question-container row">
                      <div className="col-sm-7">
                        <div className="Chat-message">
                          {qs.length > 0 && <span className="Chat-message-order">{qs[0].questionOrder}</span>}
                          {R.map(
                            q => (
                              <div key={q.question} style={{ paddingRight: qs.length > 1 ? 40 : 0 }}>
                                <p dangerouslySetInnerHTML={{ __html: q.question }} />
                                {qs.length > 1 && (
                                  <div className="Chat-stats-counts">
                                    {renderStatsCount(q.questionCount, 'x', false)}
                                    {renderStatsCount(q.finishedCount, 'x', true)}
                                  </div>
                                )}
                              </div>
                            ),
                            qs
                          )}
                        </div>
                      </div>
                      <div className="col-sm-5">
                        <div className="Chat-stats-counts">
                          {renderStatsCount(totals.questionCount, 'x', false, 'Total')}
                          {renderStatsCount(totals.finishedCount, 'x', true, 'Finished')}
                        </div>
                      </div>
                    </div>
                    {R.map(
                      a =>
                        renderMessage(`${a.answerKey}/${a.answer}`, a.answer, a.answerCount, a.finishedCount, totals),
                      R.sortWith([R.descend(R.prop('answerCount')), R.descend(R.prop('finishedCount'))])(as)
                    )}
                    {noAnswer > 0 && as.length > 0 && renderMessage('noAnswer', null, noAnswer, 0, totals, true)}
                  </div>
                );
              }, dialogueKeys)
            ) : (
              <div className="Empty-message">No message stats available</div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => {
  const { loader: { loading }, entities: { chatsStats: { currentChatStats } } } = state;
  return {
    loading,
    currentChatStats
  };
};

export default connect(mapStateToProps, chatStatsActions)(ChatStatsContainer);



// WEBPACK FOOTER //
// ./src/containers/private/ChatStatsContainer.js